import { useState, useRef, useEffect, MouseEvent } from "react"
import tw from "twin.macro"

import PlayIcon from "icons/Play"
import { BodyDetail, H5 } from "styles/Text"

export const FeaturedWord = ({ text, id, markedup_text, english, audio }) => {
  const [isActive, setIsActive] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const launcher = useRef(null)
  const audioEl = useRef(null)
  // Close the popup if you click anywhere else in the document
  useEffect(() => {
    if (!isActive) {
      return () => {}
    }
    let listener
    document.addEventListener(
      "click",
      (listener = (e) => {
        if (!e.composedPath().includes(launcher.current)) {
          setIsActive(false)
        }
      })
    )
    return () => {
      document.removeEventListener("click", listener)
    }
  }, [isActive])

  const handleClick = (e: MouseEvent) => {
    if (
      isActive &&
      e.nativeEvent.composedPath().includes(launcher.current) &&
      e.nativeEvent.composedPath()[0] !== launcher.current
    ) {
      return
    }
    setIsActive(!isActive)
  }
  const togglePlay = () => {
    if (isPlaying) {
      audioEl.current.pause()
      audioEl.current.currentTime = 0
      setIsPlaying(false)
    } else {
      audioEl.current.play()
    }
  }

  return (
    <button
      // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Mark_role#specifications
      // eslint-disable-next-line jsx-a11y/aria-role
      role="mark"
      type="button"
      tw="cursor-pointer relative"
      css={
        isActive
          ? tw`bg-primary text-black pb-2 inline-block -mb-2 px-2 rounded-xl`
          : tw`text-primary underline bg-transparent`
      }
      onClick={handleClick}
      ref={launcher}
    >
      {isActive && (
        <div
          tw="absolute bg-white rounded-2xl p-4 w-72 z-10 bottom-full mb-6 text-left flex items-center"
          css={{ boxShadow: "0 5px 10px rgba(0,0,0,0.1)" }}
        >
          {audio && (
            <div tw="flex-shrink-0 mr-4">
              <button
                type="button"
                tw="w-15 h-15 rounded-full bg-primary text-black flex items-center justify-center"
                onClick={togglePlay}
                aria-controls={`audio-player-${audio}`}
                title={`Listen to "${markedup_text}" pronunciation`}
              >
                {isPlaying ? (
                  <div tw="w-6 h-6 bg-current" />
                ) : (
                  <PlayIcon tw="ml-1 w-6 h-6" />
                )}
              </button>
              <audio
                id={`audio-player-${audio}`}
                ref={audioEl}
                onPlay={() => setIsPlaying(true)}
                onEnded={() => setIsPlaying(false)}
              >
                <source src={audio} />
                <track
                  lang="bck"
                  kind="captions"
                  src={`/api/words/${id}/bck.vtt`}
                />
              </audio>
            </div>
          )}
          <div>
            <H5 tw="text-primary mb-1 capitalize">{markedup_text}</H5>
            <BodyDetail lang="en" tw="text-med-grey">
              {english}
            </BodyDetail>
          </div>
          <div
            tw="absolute w-5 h-3 bottom-0 left-7.5 -mb-3"
            css={{
              border: "10px solid transparent",
              borderTop: "10px solid white",
              borderBottom: "0",
            }}
          />
        </div>
      )}
      {text}
    </button>
  )
}

export default FeaturedWord
